import React, { useEffect, useState } from 'react';
import { Box, Button, Modal, IconButton } from '@material-ui/core';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import EditIcon from '@material-ui/icons/Edit';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import CustomForm from '../../../components/FormRenderer/CustomForm';

import {
  sanitizePayload,
  initializeFormData,
} from '../../../components/FormRenderer/utils';
import {
  getSchoolStudents,
  addUserToSchool,
  updateBuzzOneUser,
  addStudentAndParentToSchool,
  updateBuzzOneStudent,
} from '../BuzzOneSlice';
import moment from 'moment';
import BuzzSelect from '../../../components/BuzzSelect/BuzzSelect';

export default function BuzzOneSchoolStudents() {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();

  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const { schools, students } = useSelector(({ buzzOne }) => {
    return {
      schools: buzzOne.schools,
      students: buzzOne.students,
    };
  });

  const [schoolId, setSchoolId] = useState();

  useEffect(() => {
    if (!schoolId) return;
    dispatch(getSchoolStudents({ schoolId }));
  }, [schoolId]);

  useEffect(() => {
    if (!schools || schools.length === 0) return;
    setSchoolId(schools[0]._id);
  }, [schools]);

  useEffect(() => {
    if (selectedRowId) {
      let newFormData = JSON.parse(
        JSON.stringify(students.filter((item) => item._id === selectedRowId)[0])
      );

      newFormData.parentName = newFormData.parent.name;
      newFormData.email = newFormData.parent.email;
      newFormData.mobile = newFormData.parent.mobile;

      setFormData(newFormData);
    } else setFormData(initializeFormData(formTemplate));
  }, [selectedRowId]);

  useEffect(() => {
    const formTemplate = [
      {
        id: 'form.name',
        title: 'Name',
        slug: 'name',
        type: 'String',
        required: true,
      },
      {
        id: 'form.class',
        title: 'Class',
        slug: 'class',
        type: 'String',
        required: true,
      },
      {
        id: 'form.grade',
        title: 'Grade',
        slug: 'grade',
        type: 'String',
        required: true,
      },
      {
        id: 'form.parent.name',
        title: 'Parent Name',
        slug: 'parentName',
        type: 'String',
        required: true,
      },
      {
        id: 'form.parent.email',
        title: 'Parent Email',
        slug: 'email',
        type: 'String',
        required: true,
      },
      {
        id: 'form.parent.mobile',
        title: 'Parent Mobile',
        slug: 'mobile',
        type: 'String',
        required: true,
      },
    ];
    setFormTemplate(formTemplate);

    setFormData(initializeFormData(formTemplate));
  }, []);

  const studentsColumn = [
    { Header: 'Admission Id', accessor: 'admissionNumber' },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Class', accessor: 'class' },
    { Header: 'Grade', accessor: 'grade' },
    {
      Header: 'Parent',
      Cell: (val) => {
        return (
          <>
            <p>{val.cell.row.original.parent.name}</p>
            <p>{val.cell.row.original.parent.email}</p>
            <p>{val.cell.row.original.parent.mobile}</p>
          </>
        );
      },
    },
    {
      Header: 'Parent Roles',
      Cell: (val) => {
        return (
          <>
            {val.cell.row.original.parent.scopes
              .filter(({ school }) => school._id === schoolId)
              .map(({ role, product }) => (
                <p>
                  {role} - {product}
                </p>
              ))}
          </>
        );
      },
    },
    {
      Header: 'Action',
      Cell: (val) => {
        const { _id } = val.cell.row.original;

        return (
          <>
            <IconButton onClick={() => handleEdit(_id)}>
              <EditIcon color="secondary" />
            </IconButton>
          </>
        );
      },
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const handleEdit = (id) => {
    setSelectedRowId(id);
    openFormModal();
  };

  const handleCreateNew = () => {
    setSelectedRowId(null);
    openFormModal();
  };

  const handleSave = async () => {
    handleLoader(true);
    let payload = {
      name: formData.name,
      grade: formData.grade,
      class: formData.class,
      parent: {
        name: formData.parentName,
        email: formData.email,
        mobile: formData.mobile,
      },
    };

    if (selectedRowId === null)
      await dispatch(addStudentAndParentToSchool({ schoolId, data: payload }))
        .unwrap()
        .then(() => {
          SuccessToast('Student added to School');
          dispatch(getSchoolStudents({ schoolId }));
          closeFormModal();

          handleLoader(false);
        })
        .catch((error) => {
          console.error('rejected', error);
          ErrorToast(error.message);
          return;
        });
    else {
      const { parent, ...student } = payload;

      const {
        parent: { _id },
      } = students.find((item) => item._id === selectedRowId);

      await dispatch(updateBuzzOneUser({ id: _id, data: parent })).unwrap();

      await dispatch(
        updateBuzzOneStudent({ id: selectedRowId, data: student })
      ).unwrap();

      SuccessToast('Student / Parent edited successfully');
      dispatch(getSchoolStudents({ schoolId }));
      closeFormModal();

      handleLoader(false);
    }
  };

  return (
    <Box style={{ width: '100%' }}>
      <Box
        display="flex"
        alignItems={'center'}
        justifyContent={'space-between'}
        style={{ gap: '1rem', width: '100%' }}>
        <div>
          <Button
            onClick={handleCreateNew}
            variant={'contained'}
            color="primary">
            Add Student | Parent
          </Button>
        </div>

        <div style={{ minWidth: '20%' }}>
          <BuzzSelect
            // fullWidth={false}
            name="School"
            size={'small'}
            placeholder={'Select School'}
            value={schoolId}
            onChange={(e) => {
              setSchoolId(e.target.value);
            }}
            // label="Select Type"
            valueKey="_id"
            labelKey="name"
            options={schools}
          />
        </div>
      </Box>

      <Box my={2}>
        <BuzzTable
          columns={studentsColumn}
          data={students}
          isHandlePagination={true}
        />
      </Box>
      <Modal open={showFormModal} onClose={closeFormModal}>
        <CustomForm
          formFor={'Student | Parent'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            setFormTemplate((state) => {
              return JSON.parse(JSON.stringify(state));
            });
          }}
          mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
          handleSave={handleSave}
        />
      </Modal>
    </Box>
  );
}
